import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Mic, Square, Loader2, AlertCircle, Sparkles, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reusable mic-button + recording-state for any consultation section.
// Flow:
//   1) Doctor clicks the mic — MediaRecorder captures webm/opus.
//   2) Click again (or auto-stop after MAX_DURATION) → POST to /api/transcribe.
//   3) Whisper transcript is passed to /api/voice/parse with `mode` so
//      Claude returns structured rows.
//   4) onParsed({medicines:[...], tests:[...], transcript}) bubbles up
//      so the parent decides how to merge with current state.
//
// Why this lives in its own file:
//   - Same component is reused on Prescription + Investigations.
//   - Keeps MediaRecorder lifecycle (start/stop/cleanup) in ONE place so
//     a half-finished recording can never leak across sections.
//
// Browser support:
//   - Requires getUserMedia → HTTPS only (preview & prod are both HTTPS).
//   - webm/opus is the universally-supported MediaRecorder codec.

const MAX_RECORDING_MS = 60_000;   // Hard stop at 60s — keeps Whisper bills bounded and audio < 25MB.

export default function VoiceDictationButton({
  mode = 'prescription',          // 'prescription' | 'investigation'
  inventoryHint = [],              // Optional list of names from inventory cache
  onParsed,                        // ({ medicines, tests, transcript }) => void
  disabled = false,
  size = 'sm',
  className = '',
  testId,
  label,                           // optional explicit label
}) {
  const [recording, setRecording] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [permissionError, setPermissionError] = useState(false);
  // Live transcript preview from browser Web Speech API. Runs alongside
  // MediaRecorder so the doctor sees words appear in real time — Whisper
  // still does the final high-accuracy pass.
  const [livePartial, setLivePartial] = useState('');

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const autoStopRef = useRef(null);
  const speechRecRef = useRef(null);

  // Tear-down hook — release the mic + stop any timers when the button
  // unmounts mid-recording. Otherwise the browser keeps the red mic
  // indicator on and the OS sees the tab as "using microphone".
  const cleanup = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoStopRef.current) { clearTimeout(autoStopRef.current); autoStopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (speechRecRef.current) {
      try { speechRecRef.current.stop(); } catch (_) {}
      speechRecRef.current = null;
    }
    recorderRef.current = null;
  }, []);

  useEffect(() => () => cleanup(), [cleanup]);

  // Live Web Speech API preview — best-effort, falls back gracefully on
  // Firefox (no support). Drives the floating partial-transcript bubble.
  const startLivePreview = useCallback(() => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = 'en-IN';
      rec.onresult = (e) => {
        let live = '';
        for (let i = e.resultIndex; i < e.results.length; i++) {
          live += e.results[i][0].transcript;
        }
        setLivePartial((prev) => (prev + ' ' + live).trim().slice(-300));
      };
      rec.onerror = () => {};
      rec.start();
      speechRecRef.current = rec;
    } catch (_) { /* not available */ }
  }, []);

  const startRecording = async () => {
    setPermissionError(false);
    setElapsed(0);
    setLivePartial('');
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, sampleRate: 16000 },
      });
      streamRef.current = stream;

      // Pick the best mime type the browser supports. Whisper accepts webm
      // / mp4 / wav — webm/opus is universal on Chromium + Safari 14.5+.
      let mime = 'audio/webm;codecs=opus';
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = 'audio/webm';
      }
      if (!window.MediaRecorder?.isTypeSupported?.(mime)) {
        mime = 'audio/mp4';
      }

      const recorder = new MediaRecorder(stream, { mimeType: mime });
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data?.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        cleanup();
        const blob = new Blob(chunksRef.current, { type: mime });
        chunksRef.current = [];
        setRecording(false);
        if (blob.size < 800) {
          // 800 bytes is roughly < 0.1s of audio — nothing useful to send.
          toast.info('Recording was too short. Try again.');
          return;
        }
        await sendForTranscription(blob, mime);
      };

      recorder.start(250); // gather chunks every 250ms; less drop on quick stops
      setRecording(true);
      startLivePreview();

      timerRef.current = setInterval(() => {
        setElapsed((e) => e + 1);
      }, 1000);
      autoStopRef.current = setTimeout(() => {
        if (recorderRef.current && recorderRef.current.state === 'recording') {
          recorderRef.current.stop();
          toast.info('Auto-stopped at 60 seconds.');
        }
      }, MAX_RECORDING_MS);
    } catch (err) {
      console.error('Mic error:', err);
      setPermissionError(true);
      toast.error('Microphone access denied. Please allow microphone access in your browser.');
      cleanup();
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') {
      recorderRef.current.stop();
    }
  };

  const sendForTranscription = async (blob, mime) => {
    setProcessing(true);
    try {
      // 1) Transcribe with Whisper
      const fd = new FormData();
      const filename = mime.includes('mp4') ? 'voice.mp4' : 'voice.webm';
      fd.append('audio', blob, filename);
      const trRes = await axios.post(`${API_URL}/api/transcribe`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const transcript = (trRes.data?.text || '').trim();
      if (!transcript) {
        toast.warning('Could not detect any speech. Please try again in a quieter setting.');
        return;
      }

      // 2) Parse the transcript into structured rows
      const parseRes = await axios.post(`${API_URL}/api/voice/parse`, {
        transcript,
        mode,
        inventory_hint: inventoryHint?.slice(0, 80),
      }, { withCredentials: true });

      const payload = {
        medicines: parseRes.data?.medicines || [],
        tests: parseRes.data?.tests || [],
        transcript: parseRes.data?.transcript || transcript,
      };

      if (mode === 'prescription' && payload.medicines.length === 0) {
        toast.warning(`Heard "${transcript.slice(0, 60)}..." but couldn't parse any medicines. Try again or type manually.`);
      } else if (mode === 'investigation' && payload.tests.length === 0) {
        toast.warning(`Heard "${transcript.slice(0, 60)}..." but couldn't extract any test names.`);
      } else {
        const n = mode === 'prescription' ? payload.medicines.length : payload.tests.length;
        toast.success(`Dictation captured · ${n} item${n !== 1 ? 's' : ''} parsed`);
      }
      onParsed?.(payload);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Could not transcribe. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const onClick = () => {
    if (processing) return;
    if (recording) stopRecording();
    else startRecording();
  };

  const buttonLabel = label || (mode === 'prescription' ? 'Dictate Rx' : 'Dictate Tests');
  const compact = !label && size === 'sm';

  return (
    <>
    <div className={`inline-flex items-center gap-2 ${className}`}>
      <Button
        size={size}
        variant={recording ? 'destructive' : 'outline'}
        onClick={onClick}
        disabled={disabled || processing || permissionError}
        className={`gap-1.5 transition ${
          recording ? 'animate-pulse' : ''
        } ${processing ? 'opacity-80' : ''}`}
        data-testid={testId || `voice-${mode}-btn`}
        title={recording ? 'Click to stop and transcribe' : 'Click to dictate'}
      >
        {processing ? (
          <>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {!compact && <span>Transcribing…</span>}
          </>
        ) : recording ? (
          <>
            <Square className="h-3.5 w-3.5 fill-current" />
            <span>Stop {String(elapsed).padStart(2, '0')}s</span>
          </>
        ) : (
          <>
            <Mic className="h-3.5 w-3.5" />
            {!compact && <span>{buttonLabel}</span>}
          </>
        )}
      </Button>
      {recording && (
        <Badge variant="outline" className="text-[10px] gap-1 bg-red-50 text-red-700 border-red-200 animate-pulse">
          <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
          REC
        </Badge>
      )}
      {!recording && !processing && (
        <Badge variant="outline" className="text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200 hidden md:inline-flex gap-1">
          <Sparkles className="h-2.5 w-2.5" /> AI
        </Badge>
      )}
    </div>

    {/* Live partial-transcript bubble — floats above the page so it stays
        visible no matter how deep in the consultation form the doctor is.
        Best-effort via browser Web Speech API; final accurate transcript
        comes from Whisper on stop. */}
    {recording && (
      <div
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[90] max-w-2xl w-[calc(100%-3rem)] bg-slate-900/95 backdrop-blur-md text-white rounded-2xl shadow-2xl px-5 py-3 border border-white/10"
        data-testid="voice-live-partial"
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          <span className="text-[10px] uppercase tracking-wider text-white/60 font-bold">
            Listening · {String(elapsed).padStart(2,'0')}s / 60s
          </span>
          <button
            onClick={stopRecording}
            className="ml-auto text-[10px] underline text-white/70 hover:text-white"
            data-testid="voice-stop-link"
          >
            Stop & transcribe
          </button>
        </div>
        <p className="text-sm leading-snug text-white/95 line-clamp-2">
          {livePartial || <span className="italic text-white/40">Start speaking — words will appear here as you talk.</span>}
        </p>
      </div>
    )}
    </>
  );
}

// ===================== Confirmation Modal =====================
// Shows the doctor what was heard + parsed, so they can review before the
// rows hit the prescription/investigation list. Defensive layer for the
// medico-legal scenario — keeps the doctor's eyes on every term before
// it lands in the patient record.
//
// Master-catalogue reconciliation (medico-legal):
//   • If the backend auto-matched the spoken item to the tenant master at
//     ≥90% confidence → row is "Matched" and rendered with the canonical
//     name + stock/department metadata.
//   • If confidence is below 90% but candidates exist → row is "Needs
//     review" and the doctor picks one from a dropdown before it can be
//     added to the chart.
//   • If no candidate at all → row is "Not in catalogue" and gets dropped.
//
// The "Add N items" button counts only resolved rows (matched OR picked).

export function VoiceConfirmModal({
  open, onClose, payload, mode = 'prescription', onConfirm,
}) {
  const rawItems = mode === 'prescription' ? (payload?.medicines || []) : (payload?.tests || []);

  // pickedIdx[i] = -1   → not picked yet
  //               | n   → row i resolved to its master_candidates[n]
  //               | -2  → "Add anyway" (force keep without master match)
  const [pickedIdx, setPickedIdx] = useState({});

  useEffect(() => {
    if (!open) return;
    const init = {};
    rawItems.forEach((it, i) => {
      if (it && it.master_match) init[i] = 'auto';
      else if (it && (it.master_candidates || []).length > 0) init[i] = -1;
      else init[i] = -1;
    });
    setPickedIdx(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payload]);

  if (!open || !payload) return null;

  // Build the resolved item list ready to ship to the parent.
  const resolveItem = (it, idx) => {
    const choice = pickedIdx[idx];
    if (it.master_match || choice === 'auto') {
      // Auto-matched — already has canonical name baked in by the backend.
      const m = it.master_match || {};
      if (mode === 'prescription') {
        return { ...it, medicine_name: m.name || it.medicine_name, composition: m.composition, brand: m.brand, stock: m.stock, unit: m.unit, _resolved: 'matched' };
      }
      return { ...it, name: m.name || it.name, department: m.department, code: m.code, _resolved: 'matched' };
    }
    if (typeof choice === 'number' && choice >= 0) {
      const cand = (it.master_candidates || [])[choice];
      if (!cand) return null;
      if (mode === 'prescription') {
        return { ...it, medicine_name: cand.name, composition: cand.composition, brand: cand.brand, stock: cand.stock, unit: cand.unit, _resolved: 'picked' };
      }
      return { ...it, name: cand.name, department: cand.department, code: cand.code, _resolved: 'picked' };
    }
    if (choice === 'force') {
      return { ...it, _resolved: 'force' };
    }
    return null;
  };

  const resolvedItems = rawItems.map((it, i) => resolveItem(it, i)).filter(Boolean);

  const totalResolvable = rawItems.filter((it, i) => (it && (it.master_match || (it.master_candidates || []).length > 0)) || pickedIdx[i] === 'force').length;
  const totalResolved = resolvedItems.length;
  const noMasterCount = rawItems.filter((it) => it && !it.master_match && (it.master_candidates || []).length === 0).length;

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden border-0 flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
        data-testid="voice-confirm-modal"
      >
        {/* Hero */}
        <div className="relative bg-gradient-to-br from-indigo-700 to-blue-700 text-white px-6 pt-5 pb-4 flex-shrink-0">
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-cyan-300/20 rounded-full blur-3xl" />
          <div className="flex items-center justify-between relative">
            <div className="flex items-center gap-2">
              <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center">
                <Sparkles className="h-4 w-4" />
              </div>
              <div>
                <h3 className="text-base font-bold leading-tight" style={{ fontFamily: 'Manrope' }}>
                  Voice Dictation · Master Match
                </h3>
                <p className="text-[11px] text-white/70">
                  Items below are reconciled with your {mode === 'prescription' ? 'medicine inventory' : 'investigation catalogue'} before they land in the chart.
                </p>
              </div>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="voice-confirm-close">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Transcript */}
        <div className="px-6 py-3 bg-slate-50 border-b flex-shrink-0">
          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
            What was heard
          </p>
          <p className="text-sm text-slate-800 italic leading-snug" data-testid="voice-confirm-transcript">
            "{payload.transcript || '—'}"
          </p>
        </div>

        {/* Parsed rows */}
        <div className="flex-1 px-6 py-4 overflow-y-auto min-h-0">
          {rawItems.length === 0 ? (
            <div className="text-center py-6 text-slate-400">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-300" />
              <p className="text-sm italic">
                Couldn't parse any {mode === 'prescription' ? 'medicines' : 'tests'} from this dictation.
              </p>
              <p className="text-xs mt-1">Try speaking more slowly, or type manually.</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="voice-confirm-items">
              {rawItems.map((it, i) => (
                <MatchRow
                  key={i}
                  item={it}
                  index={i}
                  mode={mode}
                  picked={pickedIdx[i]}
                  onPick={(val) => setPickedIdx((p) => ({ ...p, [i]: val }))}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t flex items-center justify-between bg-white flex-shrink-0 gap-3">
          <div className="text-[11px] text-slate-500">
            {totalResolved} of {rawItems.length} resolved
            {noMasterCount > 0 && (
              <span className="ml-1 text-amber-600">· {noMasterCount} not in catalogue</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={onClose} className="gap-1.5 text-slate-600" data-testid="voice-confirm-cancel">
              <X className="h-4 w-4" /> Cancel
            </Button>
            <Button
              onClick={() => { onConfirm?.(resolvedItems); onClose(); }}
              disabled={totalResolved === 0}
              className="bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 text-white gap-1.5"
              data-testid="voice-confirm-add"
              title={totalResolved === 0 ? 'Pick a master entry for at least one item' : ''}
            >
              <Sparkles className="h-4 w-4" />
              Add {totalResolved} {mode === 'prescription' ? 'medicine' : 'test'}{totalResolved !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MatchRow({ item, index, mode, picked, onPick }) {
  // Determine row state: matched / needs review / not in catalogue.
  const matched = !!item.master_match;
  const candidates = item.master_candidates || [];
  const hasCandidates = candidates.length > 0;
  const score = Math.round(item.match_score || 0);

  // What did the doctor originally say?
  const spoken = mode === 'prescription'
    ? (item.medicine_name || item.name || '—')
    : (item.name || '—');

  if (matched) {
    return (
      <div className="border border-emerald-200 bg-emerald-50/40 rounded-lg p-3" data-testid={`voice-match-row-${index}`}>
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="h-6 w-6 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <svg className="h-3.5 w-3.5 text-emerald-600" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-slate-900 truncate">
                {item.master_match.name}
                {mode === 'prescription' && item.dosage && <span className="ml-1.5 text-[11px] font-semibold text-slate-500">{item.dosage}</span>}
              </p>
              <p className="text-[10px] text-slate-500 truncate">
                Heard: "<span className="italic">{spoken}</span>"
                {mode === 'prescription' && item.master_match.composition && <span className="ml-1 text-blue-600 font-medium">· {item.master_match.composition}</span>}
                {mode === 'investigation' && item.master_match.department && <span className="ml-1 text-purple-600 font-medium">· {item.master_match.department}</span>}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {mode === 'prescription' && typeof item.master_match.stock === 'number' && (
              <Badge variant="outline" className={`text-[10px] ${item.master_match.stock > 0 ? 'bg-emerald-100 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                {item.master_match.stock > 0 ? `Stock ${Math.floor(item.master_match.stock)}` : 'Out'}
              </Badge>
            )}
            <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 font-bold">
              {score}%
            </Badge>
          </div>
        </div>
        {mode === 'prescription' && (
          <RxMetaBar med={item} />
        )}
      </div>
    );
  }

  // Needs review — surface picker with top candidates
  return (
    <div className={`border rounded-lg p-3 ${picked && typeof picked === 'number' && picked >= 0 ? 'border-blue-300 bg-blue-50/30' : picked === 'force' ? 'border-amber-300 bg-amber-50/30' : 'border-amber-200 bg-amber-50/40'}`} data-testid={`voice-match-row-${index}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5 text-amber-600 flex-shrink-0" />
            <p className="text-sm font-bold text-slate-900 truncate">
              "{spoken}"
              {mode === 'prescription' && item.dosage && <span className="ml-1.5 text-[11px] font-semibold text-slate-500">{item.dosage}</span>}
            </p>
          </div>
          <p className="text-[10px] text-amber-700 mt-0.5">
            {hasCandidates
              ? `Not an exact match (${score}%). Pick the right entry from the catalogue:`
              : 'Not found in your catalogue — won\'t be added unless you tick "Add anyway".'}
          </p>
        </div>
      </div>
      {hasCandidates && (
        <div className="space-y-1 mb-1.5">
          {candidates.map((c, ci) => (
            <button
              key={ci}
              type="button"
              onClick={() => onPick(ci)}
              className={`w-full text-left text-xs px-2.5 py-1.5 rounded border transition flex items-center justify-between gap-2 ${
                picked === ci
                  ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                  : 'bg-white text-slate-800 border-slate-200 hover:border-blue-400 hover:bg-blue-50/60'
              }`}
              data-testid={`voice-candidate-${index}-${ci}`}
            >
              <span className="font-medium truncate">
                {c.name}
                {mode === 'prescription' && c.composition && <span className={`ml-1.5 text-[10px] ${picked === ci ? 'text-blue-100' : 'text-blue-600'}`}>· {c.composition}</span>}
                {mode === 'investigation' && c.department && <span className={`ml-1.5 text-[10px] ${picked === ci ? 'text-purple-100' : 'text-purple-600'}`}>· {c.department}</span>}
              </span>
              <span className="flex items-center gap-1.5 flex-shrink-0">
                {mode === 'prescription' && typeof c.stock === 'number' && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${picked === ci ? 'bg-white/20 text-white' : c.stock > 0 ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
                    {c.stock > 0 ? `${Math.floor(c.stock)}` : 'Out'}
                  </span>
                )}
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${picked === ci ? 'bg-white/20 text-white' : 'bg-amber-50 text-amber-700'}`}>
                  {Math.round(c.score || 0)}%
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between gap-2 pt-1">
        <button
          type="button"
          onClick={() => onPick(picked === 'force' ? -1 : 'force')}
          className={`text-[10px] underline ${picked === 'force' ? 'text-amber-700 font-bold' : 'text-slate-500 hover:text-slate-700'}`}
          data-testid={`voice-force-add-${index}`}
        >
          {picked === 'force' ? '✓ Adding as free-text' : 'Add anyway as free-text'}
        </button>
        {(typeof picked === 'number' && picked >= 0) && (
          <button
            type="button"
            onClick={() => onPick(-1)}
            className="text-[10px] text-slate-400 hover:text-slate-600 underline"
            data-testid={`voice-clear-pick-${index}`}
          >
            Clear pick
          </button>
        )}
      </div>
    </div>
  );
}

function RxMetaBar({ med }) {
  if (!med) return null;
  const bits = [];
  if (med.frequency || med.timing) bits.push(med.timing || med.frequency);
  if (med.intake) bits.push(med.intake);
  if (med.duration) bits.push(med.duration);
  if (med.quantity) bits.push(`Qty ${med.quantity}`);
  if (bits.length === 0 && !med.remarks) return null;
  return (
    <div className="flex flex-wrap items-center gap-1 mt-1.5 pl-8">
      {bits.map((b, i) => (
        <span key={i} className="px-1.5 py-0.5 text-[10px] font-medium text-slate-600 bg-white border border-slate-200 rounded">
          {b}
        </span>
      ))}
      {med.remarks && (
        <span className="text-[10px] italic text-slate-500 truncate">"{med.remarks}"</span>
      )}
    </div>
  );
}
